/**
 * Prerender / prefetch guard
 *
 * A page load is not automatically a user-present load. Two distinct speculative
 * mechanisms exist, and each is covered by a different branch:
 *
 * - Chromium speculation-rules pre-render: the document exposes
 *   `document.prerendering === true` and fires `prerenderingchange` when it is
 *   activated. That property is Chromium-only — **Safari never implemented it**,
 *   so `isPagePrerendering()` is permanently `false` there and the
 *   `prerenderingchange` listener can never fire on Safari.
 * - Safari "Preload Top Hit in the background": the preload runs in the
 *   **background**, so the document reads `visibilityState === 'hidden'` until
 *   the user actually selects the hit. Apple's guidance is to detect these loads
 *   with the Page Visibility API, which is exactly what `isPageVisible()` does.
 *
 * So on Safari the branch that does the work is `isPageVisible()` (a human is
 * present exactly when visible), and the gate only holds if the read happens
 * while the document is still hidden. A late read — after other work on the
 * load path — narrows that window. (The `visibility-state` performance entry
 * type would be authoritative, but it is Chrome/Edge-only and unavailable on
 * Safari.)
 *
 * Anything that starts a session on load — opening a websocket, asking the
 * server to match, making a host start hosting — must wait until we know a real,
 * visible user is present. This is the single gate every load-time session
 * starter consults. It is plain DOM (no framework) so it can be unit-tested and
 * reused.
 */

/**
 * `document.prerendering` is a working-draft, Chromium-only property that is not
 * yet in the TypeScript DOM lib, so we read it through a narrow structural cast.
 */
type DocumentWithPrerendering = Document & { prerendering?: boolean };

/**
 * True while the document is being pre-rendered by Chromium speculation rules.
 * Treated as false when the API is absent (including on Safari, which never
 * implemented it), so normal browsers proceed.
 */
export function isPagePrerendering(): boolean {
  if (typeof document === 'undefined') return false;
  return (document as DocumentWithPrerendering).prerendering === true;
}

/**
 * True when the page is visible to the user.
 *
 * Fails open for this check: a browser that does not expose `visibilityState`
 * is treated as visible, so normal browsers never get stuck behind the gate.
 */
export function isPageVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === undefined || document.visibilityState === 'visible';
}

/**
 * Resolves exactly once, and only when it is safe to start a session: the page
 * is not prerendering AND it is visible.
 *
 * If the page is currently hidden, this waits for the first `visibilitychange`
 * that reports `visible`. A `prerenderingchange` also triggers a re-check, so a
 * pre-rendered page resolves when (and only when) it is activated and visible.
 * Listeners are removed as soon as the promise settles.
 */
export function whenHumanPageLoad(): Promise<void> {
  return new Promise<void>((resolve) => {
    // No DOM (SSR / non-browser): nothing to wait for.
    if (typeof document === 'undefined') {
      resolve();
      return;
    }

    let settled = false;

    const cleanup = (): void => {
      document.removeEventListener('visibilitychange', check);
      document.removeEventListener('prerenderingchange', check);
    };

    const settle = (): void => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const check = (): void => {
      if (!isPagePrerendering() && isPageVisible()) {
        settle();
      }
    };

    document.addEventListener('visibilitychange', check);
    document.addEventListener('prerenderingchange', check);
    // Handle the common case immediately, without waiting for an event.
    check();
  });
}
